import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Field, FieldProps, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { IoSettingsOutline } from 'react-icons/io5'
import { LuExternalLink } from 'react-icons/lu'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import {
  ListSavedSearchSuccessResponse,
  SavedSearchErrorResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'

interface Props {
  searchId: string
  isOpen: boolean
  name: string
  url: string
  isPrivate: boolean
  createdBy: {
    userId: string
    name: string
    email: string
  }
  onClose(): void
  getAndSetSavedSearches(): void
}

interface FormProps {
  name: string
  isPrivate: boolean
}

const ManageSavedSearchModal = ({
  searchId,
  name,
  url,
  isPrivate,
  isOpen,
  createdBy,
  onClose,
  getAndSetSavedSearches,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')
  const { user } = useContext(UserContext)
  const history = useHistory()
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const normalizeUrlForExternal = (urlToNormalize: string): string => {
    try {
      const urlObj = new URL(urlToNormalize, window.location.origin)
      if (urlObj.pathname === '/search') {
        urlObj.pathname = '/dashboard/search'
      }
      return urlObj.toString()
    } catch {
      if (urlToNormalize.startsWith('http')) {
        return urlToNormalize.replace('/search?', '/dashboard/search?')
      }
      if (urlToNormalize.startsWith('/search')) {
        return `${window.location.origin}/dashboard${urlToNormalize}`
      }
      return urlToNormalize
    }
  }

  const isMediaLibraryWidget = Boolean(
    (window as { isMediaLibraryWidget?: boolean }).isMediaLibraryWidget,
  )

  const handleViewSearchResults = (): void => {
    if (isMediaLibraryWidget) {
      const parsedUrl = new URL(url, window.location.origin)
      let pathWithinDashboard = parsedUrl.pathname.replace(
        /^\/(?:dashboard|media-library-widget)/,
        '',
      )

      if (pathWithinDashboard === '') {
        pathWithinDashboard = '/'
      }

      const pathWithSearch = `${pathWithinDashboard}${parsedUrl.search}${parsedUrl.hash}`
      history.push(pathWithSearch)
      onClose()
    } else {
      window.open(normalizeUrlForExternal(url))
    }
  }

  const deleteSavedSearch = async () => {
    setFormError('')
    const res = (await request.delete(
      `/api/media-library/${user?.clientNumber}/saved-searches/${searchId}`,
    )) as { searchId: string } | SavedSearchErrorResponse

    if (request.error) {
      setFormError(
        (res as SavedSearchErrorResponse).message ??
          t(
            'error_in_deleting_saved_search',
            'Error in deleting saved search, please try again later.',
          ),
      )
    } else {
      toast({
        description: t(
          'saved_search_deleted_successfully',
          'Saved search deleted successfully.',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
      })
      setFormError('')
      void getAndSetSavedSearches()
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          enableReinitialize
          initialValues={{
            name: name ?? '',
            isPrivate: isPrivate ?? false,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Required'),
            isPrivate: Yup.boolean().required('Required'),
          })}
          onSubmit={async values => {
            setFormError('')
            const res = (await request.put(
              `/api/media-library/${user?.clientNumber}/saved-searches/${searchId}`,
              values,
            )) as ListSavedSearchSuccessResponse | SavedSearchErrorResponse

            if (request.error) {
              setFormError(
                (res as SavedSearchErrorResponse).message ??
                  t(
                    'error_in_updating_saved_search',
                    'Error in updating saved search, please try again later.',
                  ),
              )
            } else {
              toast({
                description: t(
                  'saved_search_updated_successfully',
                  'Saved search updated successfully.',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
              })
              setFormError('')
              void getAndSetSavedSearches()
              onClose()
            }
          }}
        >
          {({ submitForm, errors, touched, isSubmitting, dirty, isValid }) => (
            <>
              <ModalHeader>
                <Icon
                  as={IoSettingsOutline}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('manage_saved_search', 'Manage saved search')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError ? (
                  <Alert status="error" mb="14px">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="name">
                  {({ field }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isRequired
                      isInvalid={Boolean(errors.name && touched.name)}
                      mb={4}
                    >
                      <FormLabel>{t('name', 'Name')}</FormLabel>
                      <Input
                        {...field}
                        placeholder={t(
                          'saved_search_name_placeholder',
                          'Enter saved search name',
                        )}
                      />
                      {errors.name && touched.name ? (
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      ) : null}
                    </FormControl>
                  )}
                </Field>
                <Button
                  variant="link"
                  color="appClearBlue"
                  {...(!isMediaLibraryWidget && {
                    rightIcon: <LuExternalLink size="16px" />,
                  })}
                  iconSpacing="4px"
                  onClick={handleViewSearchResults}
                  mb="24px"
                >
                  {t('view_search_results', 'View search results')}
                </Button>
                <Field name="isPrivate">
                  {({ field, form }: FieldProps<boolean, FormProps>) => (
                    <FormControl
                      isRequired
                      isInvalid={Boolean(
                        form.errors.isPrivate && form.touched.isPrivate,
                      )}
                      mb={4}
                    >
                      <FormLabel>{t('visibility', 'Visibility')}</FormLabel>
                      <RadioGroup
                        value={field.value ? 'true' : 'false'}
                        onChange={(value: string) => {
                          form.setFieldValue(field.name, value === 'true')
                        }}
                        isDisabled={createdBy.userId !== user?.id}
                      >
                        <VStack align="start">
                          <Radio value="true">
                            {t('only_to_me', 'Only to me')}
                          </Radio>
                          <Radio value="false">
                            {t('shared_with_everyone', 'Shared with everyone')}
                          </Radio>
                        </VStack>
                      </RadioGroup>
                      {form.errors.isPrivate && form.touched.isPrivate ? (
                        <FormErrorMessage>
                          {form.errors.isPrivate}
                        </FormErrorMessage>
                      ) : null}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Popover placement="top">
                  {({ onClose: popoverOnClose }) => (
                    <>
                      <PopoverTrigger>
                        <Button
                          colorScheme="appBrick"
                          onClick={e => {
                            e.stopPropagation()
                          }}
                        >
                          {t('delete', 'Delete')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent onClick={e => e.stopPropagation()}>
                        <PopoverArrow />
                        <PopoverCloseButton color="appSlate" />
                        <PopoverHeader color="appSlate" fontWeight="600">
                          {t('delete_saved_search', 'Delete saved search')}
                        </PopoverHeader>
                        <PopoverBody color="appSlate">
                          <VStack
                            spacing="4"
                            justifyContent="flex-start"
                            width="full"
                            alignItems="flex-start"
                          >
                            <Text fontWeight="500">
                              {t(
                                'delete_confirmation_message',
                                'Are you sure you want to delete?',
                              )}
                            </Text>
                            <Button
                              colorScheme="appBrick"
                              onClick={async () => {
                                await deleteSavedSearch()
                                popoverOnClose()
                              }}
                            >
                              {t('confirm', 'Confirm')}
                            </Button>
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </>
                  )}
                </Popover>
                <Spacer />
                <Button variant="outline" onClick={onClose}>
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  colorScheme="appBlue"
                  onClick={async () => await submitForm()}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || !dirty || !isValid}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default ManageSavedSearchModal
